import React, { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { colors } from '@guryou/colors';
import { EntityTypes } from '@guryou/core';
import { Form } from '@guryou/html-components';
import { generatePaymentIntent, getSections, stateIsNotReady, useEffectOnSuccess } from '@guryou/redux';
import { printPrice } from '@guryou/utils';
import { WidgetContext } from './WidgetContext';

const WidgetVideoDetails = ({ video }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { askFirstname, askLastname, askPhone } = useContext(WidgetContext);

  const { activeServiceId, activeTab, setActiveServiceId, user, paymentOptions } = useContext(WidgetContext);
  const { videos } = useSelector(state => state.widget);
  const sections = useSelector(state => state.sections.all);
  const [open, setOpen] = React.useState(false);
  const [tab, setTab] = React.useState(0);

  const courseOnline = paymentOptions.data.courseOnline;
  const courseOnsite = paymentOptions.data.courseOnline;
  const courseMembership = paymentOptions.data.courseMembership;

  const clickedExpand = isExpanded => {
    setActiveServiceId(isExpanded ? video.id : null);
    if (isExpanded) {
      dispatch(getSections(video.id));
    }
    if (!isExpanded) {
      dispatch(getSections(null));
    }
    setOpen(isExpanded);
  };

  useEffectOnSuccess([state => state.payments.paymentIntent], () => {
    setActiveServiceId(null);
  });

  if (stateIsNotReady(videos) || !sections) {
    return null;
  }

  return (
    <div>
      <a
        onClick={() => clickedExpand(true)}
        href="#"
        class="group relative flex h-48 flex-col overflow-hidden rounded-lg bg-gray-100 shadow-lg md:h-64 xl:h-96"
      >
        <img
          src={video.imageUrl}
          loading="lazy"
          class="absolute inset-0 h-full w-full object-cover object-center transition duration-200 group-hover:scale-110"
        />

        <div class="pointer-events-none absolute inset-0 bg-gradient-to-t from-gray-800 to-transparent md:via-transparent"></div>

        <div class="relative mt-auto p-4">
          <span class="block text-sm text-gray-200">{video?.categoryName}</span>
          <h2 class="mb-2 text-xl font-semibold text-white transition duration-100">{video?.name}</h2>

          <span class={`font-semibold text-[${colors.mandy}]`}>{t('HOME_READ_PAGE.readMore')}</span>
        </div>
      </a>
      {activeServiceId === video.id && open && (
        <div class="flex overflow-y-auto overflow-x-hidden fixed top-0 right-0 left-0 z-10 justify-center items-center w-full md:inset-0 max-h-full bg-black bg-opacity-50">
          <div class="relative p-4 w-full max-w-[720px]">
            <div class="relative bg-white rounded-lg shadow ">
              <div class="flex items-center justify-between p-4 md:p-5 border-b rounded-t">
                <h3 class="text-lg font-semibold text-gray-900">{video?.name}</h3>
                <button
                  onClick={() => {
                    setOpen(false);
                    clickedExpand(false);
                  }}
                  type="button"
                  class="text-gray-400 bg-transparent hover:bg-gray-200 hover:text-gray-900 rounded-lg text-sm w-8 h-8 ms-auto inline-flex justify-center items-center"
                >
                  <svg class="w-3 h-3" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 14 14">
                    <path
                      stroke="currentColor"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="m1 1 6 6m0 0 6 6M7 7l6-6M7 7l-6 6"
                    />
                  </svg>
                  <span class="sr-only">Close modal</span>
                </button>
              </div>
              {tab === 0 && (
                <div className="p-4 md:p-5 max-h-[80vh] overflow-y-auto">
                  <div class="h-full border-2 border-gray-200 border-opacity-60 rounded-lg overflow-hidden">
                    <img class="lg:h-48 md:h-36 w-full object-cover object-center" src={video?.imageUrl} alt="blog" />
                    <div class="p-6">
                      <h2 class="text-xs title-font font-medium text-gray-400 mb-1">{video?.categoryName}</h2>
                      <h1 class="title-font text-lg font-medium text-gray-900 mb-3">{video?.name}</h1>
                      <dd class="mb-4 font-light text-gray-500 sm:mb-5 mt-4" dangerouslySetInnerHTML={{ __html: video.description }} />
                      <div class="flex justify-end">
                        {(courseOnline || courseOnsite || courseMembership) && (
                          <button
                            onClick={() => setTab(1)}
                            type="button"
                            style={{ backgroundColor: colors.mandy }}
                            class="max-w-[160px] text-white bg-blue-700 hover:bg-blue-800 focus:ring-4 focus:outline-none focus:ring-blue-200 dark:bg-blue-600 dark:hover:bg-blue-700 dark:focus:ring-red-100 font-medium rounded-lg text-sm px-5 py-2.5 inline-flex justify-center w-full text-center"
                          >
                            {`${t('GENERAL.pay')} ${printPrice(video.price)}`}
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {tab === 1 && activeServiceId === video.id && activeTab === EntityTypes.video && (
                <Form
                  parserVersion={2}
                  actions={false}
                  onSubmit={({ email, firstname, lastname, phone }) =>
                    dispatch(generatePaymentIntent({ firstname, lastname, phone, productId: video.id, email }))
                  }
                >
                  <div class="p-4 md:p-5">
                    <ol class="relative text-gray-500 border-s border-gray-200 ml-4 pt-6">
                      <li class="mb-10 ms-6">
                        <span
                          class={`absolute flex items-center justify-center w-8 h-8 bg-green-200 rounded-full -start-4 ring-4 ring-white dark:bg-[${colors.mandy}]`}
                        >
                          <svg
                            class="w-3.5 h-3.5 text-gray-800 dark:text-white"
                            aria-hidden="true"
                            xmlns="http://www.w3.org/2000/svg"
                            fill="currentColor"
                            viewBox="0 0 20 16"
                          >
                            <path d="M18 0H2a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V2a2 2 0 0 0-2-2ZM6.5 3a2.5 2.5 0 1 1 0 5 2.5 2.5 0 0 1 0-5ZM3.014 13.021l.157-.625A3.427 3.427 0 0 1 6.5 9.571a3.426 3.426 0 0 1 3.322 2.805l.159.622-6.967.023ZM16 12h-3a1 1 0 0 1 0-2h3a1 1 0 0 1 0 2Zm0-3h-3a1 1 0 1 1 0-2h3a1 1 0 1 1 0 2Zm0-3h-3a1 1 0 1 1 0-2h3a1 1 0 1 1 0 2Z" />
                          </svg>
                        </span>
                        <h3 class="font-medium leading-tight">{t('SERVICES.personalData')}</h3>
                        <p class="text-sm">{t('SERVICES.personalDataStepDescription')}</p>

                        <div class="mt-6 grow sm:mt-8 lg:mt-4">
                          <div>
                            <div className="grid gap-4 mb-4 grid-cols-2 mt-6">
                              <input
                                type="email"
                                placeholder={t('GENERAL.email') + ', ' + t('GENERAL.necessary')}
                                name="email"
                                class={`bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-[${colors.mandy}] focus:border-[${colors.mandy}] block w-full p-2.5`}
                                value={user ? user.email : null}
                                required={true}
                              />
                              <input
                                type="text"
                                placeholder={askPhone ? t('GENERAL.phone') + ', ' + t('GENERAL.necessary') : t('GENERAL.phone')}
                                name="phone"
                                class={`bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-[${colors.mandy}] focus:border-[${colors.mandy}] block w-full p-2.5`}
                                value={user ? user.phone : null}
                                required={askPhone}
                              />
                              <input
                                type="text"
                                placeholder={askFirstname ? t('GENERAL.name') + ', ' + t('GENERAL.necessary') : t('GENERAL.name')}
                                name="firstname"
                                class={`bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-[${colors.mandy}] focus:border-[${colors.mandy}] block w-full p-2.5`}
                                value={user ? user.firstname : null}
                                required={askFirstname}
                              />
                              <input
                                type="text"
                                placeholder={askLastname ? t('GENERAL.surname') + ', ' + t('GENERAL.necessary') : t('GENERAL.surname')}
                                name="lastname"
                                class={`bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-[${colors.mandy}] focus:border-[${colors.mandy}] block w-full p-2.5`}
                                value={user ? user.lastname : null}
                                required={askLastname}
                              />
                            </div>
                          </div>
                        </div>
                      </li>
                    </ol>
                  </div>
                  <div class="flex justify-end mt-6 gap-2 p-4">
                    <button
                      type="button"
                      onClick={() => setTab(0)}
                      class="max-w-[180px] text-gray-900 bg-gray-300 font-medium rounded-lg text-sm px-5 py-2.5 inline-flex justify-center w-full text-center"
                    >
                      {t('GENERAL.goBack')}
                    </button>
                    <button
                      type="submit"
                      style={{ backgroundColor: colors.mandy }}
                      class="max-w-[180px] text-white bg-blue-700 hover:bg-blue-800 focus:ring-4 focus:outline-none focus:ring-blue-200 dark:bg-blue-600 dark:hover:bg-blue-700 dark:focus:ring-red-100 font-medium rounded-lg text-sm px-5 py-2.5 inline-flex justify-center w-full text-center"
                    >
                      {`${t('GENERAL.pay')} ${printPrice(video.price)}`}
                    </button>
                  </div>
                </Form>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WidgetVideoDetails;
